import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CourtLines, FullCourtLines } from './CourtCanvas';
import { Play, Pause, ChevronLeft, ChevronRight, Repeat, Volume2, VolumeX, Gauge } from 'lucide-react';
import { TID } from '@/constants/testIds';
import { useLang } from '@/context/LangContext';
import { normalizePath } from '@/lib/playPaths';
import { api } from '@/lib/api';
import { speak as ttsSpeak, cancelTTS, primeTTS } from '@/lib/tts';

const GOLD = '#D4AF37';
const RED = '#E11D48';
const TRANSITION_MS = 1500; // tween per step (clear but not draggy)
const STEP_HOLD_MS = 700;   // hold between steps (snappier on resume)
const LOOP_RESTART_DELAY_MS = 1200;

function speakStep(text, langCode, onEnd, rateMul = 1) {
  if (!text) { onEnd?.(); return; }
  // Base rate 0.88; multiplier comes from the on-screen speed control.
  const rate = Math.max(0.4, Math.min(2.5, 0.88 * rateMul));
  ttsSpeak({ text, lang: langCode, onEnd, rate });
}

/** Sample a point at progress t∈[0,1] along a polyline using cumulative arc length. */
function samplePath(points, t) {
  if (!points || points.length === 0) return null;
  if (points.length === 1) return { x: points[0].x, y: points[0].y };
  const lens = [0];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
    lens.push(total);
  }
  if (total === 0) return { x: points[0].x, y: points[0].y };
  const target = Math.max(0, Math.min(1, t)) * total;
  for (let i = 1; i < lens.length; i++) {
    if (lens[i] >= target) {
      const segLen = lens[i] - lens[i - 1] || 1;
      const segT = (target - lens[i - 1]) / segLen;
      return {
        x: points[i - 1].x + (points[i].x - points[i - 1].x) * segT,
        y: points[i - 1].y + (points[i].y - points[i - 1].y) * segT,
      };
    }
  }
  return { x: points[points.length - 1].x, y: points[points.length - 1].y };
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function arrowPath(a, V, Vy) {
  return {
    x1: (a.from.x / 100) * V,
    y1: (a.from.y / 100) * Vy,
    x2: (a.to.x / 100) * V,
    y2: (a.to.y / 100) * Vy,
  };
}

function ArrowMarkerDefs() {
  return (
    <defs>
      <marker id="arrow-move" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill={GOLD} />
      </marker>
      <marker id="arrow-pass" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#60A5FA" />
      </marker>
      <marker id="arrow-dribble" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill={RED} />
      </marker>
      <marker id="arrow-screen" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#10B981" />
      </marker>
    </defs>
  );
}

function Arrow({ a, V, Vy, opacity = 1 }) {
  const { x1, y1, x2, y2 } = arrowPath(a, V, Vy);
  const type = a.type || 'move';
  const stroke = type === 'pass' ? '#60A5FA' : type === 'dribble' ? RED : type === 'screen' ? '#10B981' : GOLD;
  const dasharray = type === 'pass' ? '12 8' : type === 'dribble' ? '4 6' : 'none';
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={stroke}
      strokeWidth="4"
      strokeDasharray={dasharray}
      strokeLinecap="round"
      markerEnd={`url(#arrow-${type})`}
      opacity={opacity}
      vectorEffect="non-scaling-stroke"
      style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' }}
    />
  );
}

function PlayerToken({ p, x, y, V, rosterMap, opacity = 1, tokenScaleX = 1 }) {
  const isOff = p.team === 'offense';
  const stroke = isOff ? GOLD : RED;
  const fill = '#0A192F';
  const rosterPlayer = rosterMap?.[p.player_id];
  const photoUrl = rosterPlayer?.photo_url || p.photo_url;
  const label = rosterPlayer?.number != null ? String(rosterPlayer.number) : p.label;
  const name = rosterPlayer?.name || p.player_name;

  return (
    <g transform={`translate(${x},${y}) scale(${tokenScaleX}, 1)`} opacity={opacity}>
      <circle r="38" fill={fill} stroke={stroke} strokeWidth="3.5" />
      {photoUrl ? (
        <>
          <defs>
            <clipPath id={`clip-${p.id}`}>
              <circle r="34" />
            </clipPath>
          </defs>
          <image href={photoUrl} x="-34" y="-34" width="68" height="68" clipPath={`url(#clip-${p.id})`} preserveAspectRatio="xMidYMid slice" />
          <circle cx="28" cy="-24" r="13" fill={stroke} />
          <text x="28" y="-19" textAnchor="middle" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="16" fill="#0A192F">{label}</text>
        </>
      ) : (
        <text textAnchor="middle" dy="10" fontFamily="Oswald, sans-serif" fontWeight="700" fontSize="30" fill={stroke}>
          {label}
        </text>
      )}
      {name && isOff && (
        <g>
          <rect x="-58" y="44" width="116" height="22" rx="4" fill="#0A192F" stroke={stroke} strokeWidth="1.5" />
          <text x="0" y="60" textAnchor="middle" fontFamily="IBM Plex Sans, sans-serif" fontSize="13" fontWeight="600" fill="#fff">
            {name.length > 16 ? name.slice(0, 14) + '…' : name}
          </text>
        </g>
      )}
    </g>
  );
}

function indexById(players = []) {
  const m = {};
  players.forEach(p => { m[p.id] = p; });
  return m;
}

/**
 * Animated play viewer with smooth interpolated player movement between steps.
 *
 * `courtMode` (default 'half') drives:
 *   - viewBox dimensions (1000x1000 vs 1000x2000)
 *   - which court-lines component renders
 *   - whether we stretch the SVG to fill ('none' vs 'xMidYMid meet')
 *   - a runtime-computed `tokenScaleX` that counter-stretches every player
 *     token, arrow head and badge so circles stay circular even when the
 *     full court is stretched to the screen width.
 */
export default function PlayAnimation({ play, rosterMap = {}, autoplay = false, courtMode: courtModeProp, narrate: narrateProp }) {
  const { lang, t } = useLang();
  // courtMode resolution priority:
  //   1) explicit prop (used by editor preview)
  //   2) play.court_mode (persisted on save — once we start writing it)
  //   3) heuristic: any player or step position with y > 50 outside the
  //      bottom half hints at full court — but to keep the legacy half-court
  //      plays rendering as before, we default to 'half'.
  const courtMode = courtModeProp || play?.court_mode || 'half';
  const isFull = courtMode === 'full';
  const V = 1000;
  const Vy = isFull ? 2 * V : V;

  const svgRef = useRef(null);
  const [tokenScaleX, setTokenScaleX] = useState(1);
  useEffect(() => {
    if (!isFull) { setTokenScaleX(1); return undefined; }
    const recompute = () => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const xScale = rect.width / V;
      const yScale = rect.height / Vy;
      const ratio = xScale > 0 ? yScale / xScale : 1;
      setTokenScaleX(ratio);
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    if (svgRef.current) ro.observe(svgRef.current);
    window.addEventListener('orientationchange', recompute);
    window.addEventListener('resize', recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [isFull, Vy]);

  const [stepIdx, setStepIdx] = useState(0);
  const [fromStepIdx, setFromStepIdx] = useState(0);
  const [playing, setPlaying] = useState(autoplay);
  const [progress, setProgress] = useState(1);
  const [loopMode, setLoopMode] = useState(true);
  const [narrateState, setNarrate] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage?.getItem('pm_narrate') === '1';
  });
  // When parent passes an explicit narrate prop (e.g. IQ Simulator with its
  // own TTS toggle), it wins over the internal localStorage state.
  const narrate = (typeof narrateProp === 'boolean') ? narrateProp : narrateState;
  // Animation + speech speed multiplier (persisted). 1.0 = default coach pace.
  const [speed, setSpeed] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const v = parseFloat(window.localStorage?.getItem('pm_play_speed') || '1');
    return [0.5, 1, 1.5, 2].includes(v) ? v : 1;
  });
  const persistSpeed = (v) => {
    setSpeed(v);
    try { window.localStorage?.setItem('pm_play_speed', String(v)); } catch {}
    // If we're currently speaking, restart the utterance at the new rate.
    if (window.speechSynthesis?.speaking) cancelTTS();
    lastSpokenRef.current = '';
  };
  // Two-pass narrated playback: first pass slow with voice, second pass fast for visual reinforcement
  const [phase, setPhase] = useState('narrated'); // 'narrated' | 'fast'
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const lastSpokenRef = useRef('');

  const steps = play?.steps || [];
  const fromStep = steps[fromStepIdx] || steps[0];
  const toStep = steps[stepIdx] || steps[0];

  // ===== Auto-translate step descriptions =====
  // Plays are usually authored in Spanish. If the UI lang differs from the
  // play's original language, request a translation from the backend (cached
  // per play+lang on the server). While loading we fall back to the original
  // text. This fixes the UX where the English voice was reading Spanish text.
  const [translation, setTranslation] = useState(null);
  useEffect(() => {
    setTranslation(null);
    if (!play?.id) return;
    // Skip the LLM round-trip entirely when the UI lang matches the play source
    const sourceLang = (play.original_lang || 'es').toLowerCase();
    if (lang === sourceLang) return;
    const ctrl = { cancelled: false };
    api.post(`/translate/play/${play.id}?lang=${lang}`)
      .then(r => {
        if (ctrl.cancelled) return;
        const t = r.data?.translation;
        // Build a map: step_number → translated description
        if (t && Array.isArray(t.steps)) {
          const map = {};
          for (const s of t.steps) {
            if (s.step_number != null) map[s.step_number] = s.description || '';
          }
          setTranslation({ name: t.name, description: t.description, byStep: map });
        }
      })
      .catch(() => {}); // silent: fall back to original text
    return () => { ctrl.cancelled = true; };
  }, [play?.id, play?.original_lang, lang]);

  const describe = (s) => {
    if (!s) return '';
    let text = (translation && translation.byStep[s.step_number]) || s.description || '';
    // Player-name enrichment — when the play step carries real roster info
    // (assigned via "Añadir Roster" in the editor) we replace generic
    // "jugador N" / "el N" / "player N" tokens with each player's first
    // name so the narration sounds like a real broadcast.
    const focal = (s.players || [])
      .filter(p => p.team === 'offense' && p.player_name)
      .sort((a, b) => Number(a.label) - Number(b.label));
    if (focal.length > 0) {
      focal.forEach((p, idx) => {
        const n = idx + 1;
        const firstName = (p.player_name || '').split(' ')[0];
        if (!firstName) return;
        text = text
          .replace(new RegExp(`\\bjugador\\s+${n}\\b`, 'gi'), firstName)
          .replace(new RegExp(`\\bel\\s+${n}\\b`, 'gi'), firstName)
          .replace(new RegExp(`\\bplayer\\s+${n}\\b`, 'gi'), firstName);
      });
      if (!text.trim() && s.step_number === 1) {
        const names = focal.map(p => p.player_name).join(', ');
        text = `En cancha: ${names}.`;
      }
    }
    return text;
  };

  // Speech lang ALWAYS matches the text we display (so pronunciation is right)
  const speechLang = translation ? lang : (play?.original_lang || 'es');

  // Per-step transition timing: account for each path's speed AND delay
  // so the slowest+latest player still completes by progress=1.
  // The animation must stay snappy even when narration is on with a very
  // long enriched description — otherwise the user perceives it as frozen.
  // We cap the duration to MAX_TRANSITION_MS regardless of speech length.
  const MAX_TRANSITION_MS = 6000;
  const stepTiming = useMemo(() => {
    const entries = Object.entries(fromStep?.paths || {})
      .map(([pid, raw]) => [pid, normalizePath(raw)])
      .filter(([, p]) => p);
    let maxMs = TRANSITION_MS;
    for (const [, p] of entries) {
      const tt = (p.delay || 0) * 1000 + TRANSITION_MS / Math.max(0.2, p.speed || 1);
      if (tt > maxMs) maxMs = tt;
    }
    // NOTE: previously we scaled to ~80% of speech length so the voice
    // synced with movement. That broke with the new enriched narrations
    // (220-360 chars → ~25s per step). Now: the animation always runs
    // at its natural pace, and the voice keeps talking over the visual
    // pause (handled in the narrated useEffect).
    // Divide by `speed` so 2x makes the animation finish in half the time.
    return { effectiveMs: Math.min(MAX_TRANSITION_MS, maxMs) / Math.max(0.1, speed) };
  }, [fromStep, speed]);
  const effectiveTransitionMs = stepTiming.effectiveMs;

  // animate progress 0→1 whenever stepIdx changes (and we have a different fromStep)
  useEffect(() => {
    if (fromStepIdx === stepIdx) return;
    // If user paused mid-transition: freeze and cancel any pending rAF so the
    // visual stops at the current frame (no ghost movement after Pause press).
    if (!playing) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    let cancelled = false;
    startRef.current = null;
    const dur = effectiveTransitionMs;
    const startProgress = progress < 1 ? progress : 0;
    const tick = (ts) => {
      if (cancelled) return;
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const p = Math.min(1, startProgress + elapsed / dur);
      setProgress(p);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setFromStepIdx(stepIdx);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, fromStepIdx, effectiveTransitionMs, playing]);

  // autoplay: advance after transition + hold; loop / phase-switch as needed
  useEffect(() => {
    if (!playing) return;
    // In narrated phase we hand control to speech (onend triggers next step).
    if (narrate && phase === 'narrated') return;

    // Fast pass: shorter pacing for visual reinforcement, no narration.
    const isFast = phase === 'fast';
    const transMs = isFast ? Math.min(800, effectiveTransitionMs) : effectiveTransitionMs;
    const holdMs = isFast ? 250 : STEP_HOLD_MS;

    if (stepIdx >= steps.length - 1) {
      if (isFast) {
        // Fast pass complete → exit phase
        const t = setTimeout(() => {
          setPhase('narrated');
          if (loopMode && steps.length > 1) { setFromStepIdx(0); setStepIdx(0); }
          else { setPlaying(false); }
        }, transMs + 700);
        return () => clearTimeout(t);
      }
      if (loopMode && steps.length > 1) {
        const t = setTimeout(() => setStepIdx(0), transMs + LOOP_RESTART_DELAY_MS);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPlaying(false), transMs + 1200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStepIdx(i => Math.min(i + 1, steps.length - 1)), transMs + holdMs);
    return () => clearTimeout(t);
  }, [playing, stepIdx, steps.length, loopMode, effectiveTransitionMs, narrate, phase]);

  // Track latest `playing` value so the TTS onend callback (which may fire after
  // the user has paused) can short-circuit instead of forcing another step.
  const playingRef = useRef(playing);
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Narrated pass: speak the current step description; on `onend` advance OR switch to fast pass.
  useEffect(() => {
    if (!playing || !narrate || phase !== 'narrated') return;
    const desc = describe(toStep);
    if (!desc) return;
    // Wait until the visual tween settled so voice matches what is on screen
    if (progress < 1) return;
    const sig = desc + '|' + stepIdx + '|narrated';
    if (lastSpokenRef.current === sig) return;
    lastSpokenRef.current = sig;
    speakStep(desc, speechLang, () => {
      // If user paused while we were speaking, do NOT advance.
      if (!playingRef.current) return;
      // Short breath then advance
      setTimeout(() => {
        if (!playingRef.current) return;
        if (stepIdx >= steps.length - 1) {
          // Educational pass done → switch to fast visual pass starting from step 0
          setPhase('fast');
          setFromStepIdx(0);
          setStepIdx(0);
        } else {
          setStepIdx(i => Math.min(i + 1, steps.length - 1));
        }
      }, 250);
    }, speed);
  }, [playing, narrate, phase, progress, stepIdx, toStep, speechLang, translation, steps.length, speed]);

  // Cancel any pending speech when component unmounts or narration toggles off
  useEffect(() => {
    if (!narrate) cancelTTS();
    return () => { cancelTTS(); };
  }, [narrate]);

  // When the UI language changes, cancel any utterance from the previous voice
  // so the next narrated step starts cleanly in the new locale.
  useEffect(() => {
    cancelTTS();
    lastSpokenRef.current = '';
  }, [speechLang]);

  const persistNarrate = (v) => {
    setNarrate(v);
    try { window.localStorage?.setItem('pm_narrate', v ? '1' : '0'); } catch { /* ignore */ }
    if (v) {
      // User gesture — prime engine for the current language so the very
      // first sentence speaks in the correct locale (no Spanish-default fallback).
      primeTTS(speechLang);
      const desc = describe(toStep);
      if (desc) {
        speakStep(desc, speechLang);
        lastSpokenRef.current = desc + '|' + stepIdx;
      }
    } else {
      cancelTTS();
    }
  };

  const fromMap = useMemo(() => indexById(fromStep?.players || []), [fromStep]);
  const toMap = useMemo(() => indexById(toStep?.players || []), [toStep]);

  // Compute interpolated render list: union of from+to players matched by id
  const allIds = useMemo(() => {
    const s = new Set();
    Object.keys(fromMap).forEach(k => s.add(k));
    Object.keys(toMap).forEach(k => s.add(k));
    return Array.from(s);
  }, [fromMap, toMap]);

  if (!toStep) {
    return <div className="text-gray-400 p-8 text-center">No hay pasos para mostrar.</div>;
  }

  const eased = easeInOutCubic(progress);
  const transitioning = progress < 1;

  const goPrev = () => { cancelTTS(); lastSpokenRef.current = ''; setPlaying(false); setStepIdx(i => Math.max(0, i - 1)); };
  const goNext = () => { cancelTTS(); lastSpokenRef.current = ''; setPlaying(false); setStepIdx(i => Math.min(steps.length - 1, i + 1)); };
  const togglePlay = () => {
    const willPlay = !playing;
    // PAUSE: kill any ongoing voice + cancel the pending rAF tick so the UI
    // freezes instantly. The user reported the pause button felt "stuck".
    if (!willPlay) {
      cancelTTS();
      lastSpokenRef.current = '';
      cancelAnimationFrame(rafRef.current);
      setPlaying(false);
      return;
    }
    if (stepIdx >= steps.length - 1) { setFromStepIdx(0); setStepIdx(0); }
    setPhase('narrated');
    lastSpokenRef.current = '';
    cancelTTS(); // ensure clean start
    setPlaying(true);
    if (narrate) {
      // Prime on every play-press so the very first sentence is in the right voice.
      primeTTS(speechLang);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="relative rounded-lg overflow-hidden border-2 border-gold/60 bg-navy-deep">
        <svg
          ref={svgRef}
          viewBox={isFull ? `0 0 ${V} ${Vy}` : `0 0 ${V} ${V}`}
          className="w-full block mx-auto"
          preserveAspectRatio="xMidYMid meet"
          style={{
            background: 'radial-gradient(ellipse at center top, #0E2440, #0A192F 70%)',
            // Cap render height so a full-court SVG (2:1 ratio) doesn't push
            // the page controls off-screen on tablets and phones. Half-court
            // never needs this — its 1:1 ratio is shorter than the viewport.
            maxHeight: isFull ? '70vh' : 'none',
            // Keep h-auto behaviour without Tailwind's class so the maxHeight
            // above wins when the auto-height would otherwise exceed it.
            height: 'auto',
          }}
        >
          {isFull ? <FullCourtLines /> : <CourtLines />}
          <ArrowMarkerDefs />
          {/* Strokes (only static, from target step) */}
          {(toStep.strokes || []).map(s => (
            <polyline
              key={s.id}
              points={s.points.map(pt => `${(pt.x / 100) * V},${(pt.y / 100) * Vy}`).join(' ')}
              fill="none" stroke={s.color || GOLD} strokeWidth={s.width || 3}
              strokeLinecap="round" strokeLinejoin="round"
              opacity={transitioning ? 0.35 : 1}
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Arrows: source fades out, destination fades in */}
          {(fromStep.arrows || []).map((a, i) => (
            <Arrow key={`fa-${i}`} a={a} V={V} Vy={Vy} opacity={1 - eased} />
          ))}
          {(toStep.arrows || []).map((a, i) => (
            <Arrow key={`ta-${i}`} a={a} V={V} Vy={Vy} opacity={eased} />
          ))}
          {/* Player movement paths from the source step (faint trail) */}
          {Object.entries(fromStep?.paths || {}).map(([pid, raw]) => {
            const np = normalizePath(raw); if (!np) return null;
            return (
              <polyline
                key={`anim-path-${pid}`}
                points={np.points.map(p => `${(p.x / 100) * V},${(p.y / 100) * Vy}`).join(' ')}
                fill="none" stroke={GOLD} strokeWidth="4"
                strokeLinecap="round" strokeLinejoin="round"
                strokeDasharray="2 10"
                opacity={transitioning ? 0.55 : 0.25}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          {/* Players: interpolated by id, following per-player path if defined */}
          {allIds.map((id) => {
            const fp = fromMap[id];
            const tp = toMap[id];
            const ref = tp || fp;
            const np = normalizePath(fromStep?.paths?.[id]);
            let nx, ny;
            if (transitioning && np && np.points.length > 1 && fp) {
              // Each path has its own speed + delay. We translate global
              // progress into player-local progress.
              const elapsedMs = progress * effectiveTransitionMs - (np.delay || 0) * 1000;
              const durationMs = TRANSITION_MS / Math.max(0.2, np.speed || 1);
              const localT = Math.min(1, Math.max(0, elapsedMs / durationMs));
              const s = samplePath(np.points, easeInOutCubic(localT));
              nx = s.x; ny = s.y;
            } else {
              const x1 = (fp?.position?.x ?? tp?.position?.x ?? 50);
              const y1 = (fp?.position?.y ?? tp?.position?.y ?? 50);
              const x2 = (tp?.position?.x ?? fp?.position?.x ?? 50);
              const y2 = (tp?.position?.y ?? fp?.position?.y ?? 50);
              nx = x1 + (x2 - x1) * eased;
              ny = y1 + (y2 - y1) * eased;
            }
            const x = (nx / 100) * V;
            const y = (ny / 100) * Vy;
            let opacity = 1;
            if (!fp && tp) opacity = eased; // appearing
            if (fp && !tp) opacity = 1 - eased; // disappearing
            return (
              <PlayerToken key={id} p={ref} x={x} y={y} V={V} rosterMap={rosterMap} opacity={opacity} tokenScaleX={tokenScaleX} />
            );
          })}
        </svg>

        {/* Step indicator */}
        <div className="absolute top-3 left-3 bg-navy-deep/90 border border-gold/40 rounded-md px-3 py-1 text-xs font-mono text-gold tracking-widest">
          STEP {toStep.step_number} / {steps.length}
        </div>
      </div>

      {/* Step description */}
      <div className="bg-card-steel border border-gold/30 rounded-md p-4">
        <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">
          {t('step')} {toStep.step_number}
        </div>
        <p className="text-white">{describe(toStep)}</p>
      </div>

      {/* Controls — responsive: icon-only on narrow phones to avoid overflow */}
      <div className="flex items-center justify-between gap-1.5 sm:gap-3 flex-wrap">
        <button
          data-testid={TID.playStepPrev}
          onClick={goPrev}
          aria-label={t('prev')}
          className="flex items-center gap-1 px-2.5 sm:px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">{t('prev')}</span>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-2 order-3 sm:order-none w-full sm:w-auto justify-center mt-1 sm:mt-0 flex-wrap">
          <button
            data-testid={TID.playAnimatePlay}
            onClick={togglePlay}
            className="flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light transition"
          >
            {playing ? <><Pause className="w-4 h-4" /> {t('pause')}</> : <><Play className="w-4 h-4" /> {t('play')}</>}
          </button>
          <button
            data-testid="play-narrate-toggle"
            onClick={() => persistNarrate(!narrate)}
            title={narrate ? t('voice_on_hint') : t('voice_off_hint')}
            aria-label={narrate ? t('voice') : t('muted')}
            className={`flex items-center gap-1 px-2.5 sm:px-3 py-2.5 sm:py-3 rounded-md border transition ${
              narrate ? 'bg-gold/15 border-gold text-gold' : 'border-gold/30 text-gray-400 hover:border-gold/60'
            }`}
          >
            {narrate ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            <span className="text-[10px] font-h uppercase tracking-wider hidden md:inline">
              {narrate ? t('voice') : t('muted')}
            </span>
          </button>
          <button
            data-testid="play-loop-toggle"
            onClick={() => setLoopMode(l => !l)}
            title={loopMode ? t('loop_on_hint') : t('loop_off_hint')}
            aria-label={loopMode ? t('loop_label') : t('off_label')}
            className={`flex items-center gap-1 px-2.5 sm:px-3 py-2.5 sm:py-3 rounded-md border transition ${
              loopMode ? 'bg-gold/15 border-gold text-gold' : 'border-gold/30 text-gray-400 hover:border-gold/60'
            }`}
          >
            <Repeat className="w-4 h-4" />
            <span className="text-[10px] font-h uppercase tracking-wider hidden md:inline">
              {loopMode ? t('loop_label') : t('off_label')}
            </span>
          </button>
          {/* Speed selector — adjusts both animation duration AND TTS rate */}
          <div
            className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-1.5 sm:py-2 rounded-md border border-gold/30"
            data-testid="play-speed-selector"
            role="group"
            aria-label={t('speed')}
          >
            <Gauge className="w-3.5 h-3.5 text-gold hidden sm:inline" />
            {[0.5, 1, 1.5, 2].map(v => (
              <button
                key={v}
                data-testid={`play-speed-${v}x`}
                onClick={() => persistSpeed(v)}
                className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] font-h uppercase tracking-wider font-bold transition ${
                  speed === v
                    ? 'bg-gold text-navy'
                    : 'text-gray-400 hover:text-gold'
                }`}
              >
                {v}x
              </button>
            ))}
          </div>
        </div>

        <button
          data-testid={TID.playStepNext}
          onClick={goNext}
          aria-label={t('next')}
          className="flex items-center gap-1 px-2.5 sm:px-4 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 transition"
        >
          <span className="hidden sm:inline">{t('next')}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Steps mini-track */}
      <div className="flex gap-1.5">
        {steps.map((_, i) => (
          <button
            key={i}
            onClick={() => { setPlaying(false); setStepIdx(i); }}
            className={`flex-1 h-1.5 rounded-full transition-all ${i <= stepIdx ? 'bg-gold' : 'bg-steel'}`}
            aria-label={`Step ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
